import React, { useEffect, useRef, useState } from "react"
import { useMatch, useNavigate } from "react-router-dom"
import { Modal } from "ui/Modal"
import dayjs from "dayjs"
import { Form, Button, Spin, Flex, Grid, Drawer } from "antd"
import { CustomForm } from "ui/Form"
import { ResponsivePage } from "ui/ResponsivePage"
import { useTranslation } from "react-i18next"
import { useSelector, useDispatch } from "react-redux"
import { serialize } from "object-to-formdata"
import { saveClient, cleanClient, getGuideClient } from "store/clients"
import { pick } from "lodash"
import { routes } from "router"
import styles from "./ClientForm.module.css"
import warningIcon from "assets/icons/warningIcon.svg"
import ClientForm from "./ClientForm"
import { CloseOutlined, LoadingOutlined } from "@ant-design/icons"
import { toaster } from "components/common/Toast"

const ClientWindow = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const isNew = useMatch(routes.guideClientNewPath())
  const isShow = useMatch(routes.guideClientPath())
  const isEdit = useMatch(routes.guideClientEditPath())
  const isPersisted = !isNew && (isShow || isEdit)
  const clientId = isPersisted?.params?.id
  const [isVisible, setIsVisible] = useState(true)
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const client = useSelector((state) => state.clients.client)
  const { useBreakpoint } = Grid
  const [loading, setLoading] = useState(false)

  const screens = useBreakpoint()
  const isMobile = !screens.md
  const isInitialSetRef = useRef(false)

  useEffect(() => {
    setLoading(true)
    if (clientId) {
      dispatch(getGuideClient(clientId))
    } else {
      dispatch(cleanClient())
      isInitialSetRef.current = true
    }
    setLoading(false)
    return () => dispatch(cleanClient())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId])

  useEffect(() => {
    if (client?.id && client.user && !isInitialSetRef.current) {
      const initialValues = {
        ...client.user,
        avatar: client.user.avatar || null,
        phone_number: {
          fullNumber: client.user.phone_number || "",
          countryCode: client.user.country_code || ""
        },
        country_code: client.user.country_code || "",
        state: client.user.state || "",
        country: client.user.country || "",
        street_address: client.user.address_line_1 || "",
        zip_code: client.user.zip_code || "",
        birthday: client.user.birthday ? dayjs(client.user.birthday) : null,
        client_groups: client.client_groups || []
      }
      form.setFieldsValue(initialValues)
      setIsDirty(false)
      isInitialSetRef.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client])

  // Helper to build user attributes
  const buildUserAttributes = (values) => {
    const user_attributes = pick(values, [
      "id",
      "nickname",
      "avatar",
      "gender",
      "first_name",
      "last_name",
      "email",
      "phone_number",
      "country_code",
      "birthday",
      "address_line_1",
      "address_line_2",
      "city",
      "state",
      "country",
      "zip_code"
    ])
    if (user_attributes.avatar && !(user_attributes.avatar instanceof File)) {
      delete user_attributes.avatar
    }
    if (!values.email) {
      user_attributes.no_email = true
    }
    return user_attributes
  }

  // Helper to build group clients attributes
  const buildGroupClientsAttributes = (client_groups) =>
    client_groups?.map((group) => ({
      group_id: group?.group_id || group?.id
    })) || []

  // Helper to build notes attributes
  const buildNotesAttributes = (notes) => (notes ? notes.map((note) => ({ content: note.content })) : [])

  // Helper to handle errors
  const handleFormErrors = (err, clientId, form, t) => {
    if (clientId) {
      toaster.error(t("client.client_failed", { name: form?.getFieldValue("first_name") }))
    }
    if (err?.data?.errors?.email) {
      form.setFields([{ name: "email", errors: [err.data.errors.email] }])
    }
    if (err?.data?.errors?.avatar) {
      form.setFields([{ name: "avatar", errors: [err.data.errors.avatar] }])
    }
  }

  const handleSubmit = async () => {
    try {
      const formValues = await form.validateFields()
      const invalidFields = form.getFieldsError().filter(({ errors }) => errors.length)
      if (invalidFields.length) {
        for (const { name } of invalidFields) {
          form.setFieldsTouched([name], true)
        }
        return
      }
      setLoading(true)
      if (formValues?.street_address) formValues.address_line_1 = formValues.street_address
      if (clientId) formValues.id = client.user.id

      const values = {
        ...formValues,
        phone_number: formValues.phone_number?.fullNumber,
        country_code: formValues.phone_number?.countryCode,
        birthday: formValues.birthday ? formValues.birthday.format("YYYY-MM-DD") : null
      }

      const user_attributes = buildUserAttributes(values)
      const group_clients_attributes = buildGroupClientsAttributes(values.client_groups)
      const notes_attributes = buildNotesAttributes(values.notes)

      let guide_client = {
        cash_payment: values.cash_payment,
        group_clients_attributes,
        notes_attributes,
        user_attributes,
        is_manual_address: formValues?.manualAddress || false
      }

      const formData = serialize({ guide_client }, { indices: true })
      const response = await dispatch(saveClient(clientId, formData))
      if (response) {
        if (clientId) {
          toaster.success(t("client.client_updated", { name: values?.first_name }))
        } else {
          toaster.success(response.message)
        }
        setIsVisible(false)
        navigate(-1)
      } else {
        setLoading(false)
      }
    } catch (err) {
      handleFormErrors(err, clientId, form, t)
      setLoading(false)
    }
  }

  const handleCancel = () => {
    if (isDirty) {
      setShowDiscardConfirm(true)
    } else {
      closeForm()
    }
  }

  const closeForm = () => {
    setIsVisible(false)
    navigate(-1)
  }

  const handleDiscardConfirm = () => {
    form.resetFields()
    setIsDirty(false)
    setShowDiscardConfirm(false)
    closeForm()
  }

  const handleDiscardCancel = () => {
    setShowDiscardConfirm(false)
  }

  let content
  const loadingContent = loading ? (
    <Flex key={loading} justify="center" align="center" className={`position-absolute ${styles.modalLoaderWrapper}`}>
      <Spin className="sa" indicator={<LoadingOutlined spin />} size="large" />
    </Flex>
  ) : null
  if (isMobile) {
    content = (
      <ResponsivePage onBackClick={handleCancel} pageTitle={isEdit ? t("client.edit") : t("client.add")}>
        <CustomForm form={form} layout="vertical" onValuesChange={() => { if (isInitialSetRef.current) setIsDirty(true) }}>
          <ClientForm form={form} />
          {loadingContent}
        </CustomForm>
        <div className="mb-xs">
          <Flex gap={10} className={styles.formModalFooter}>
            <Button key="cancel" variant="outlined" onClick={handleCancel} size="large" className="w-full">
              {t("global.cancel")}
            </Button>
            <Button className="w-full" key="submit" type="primary" onClick={handleSubmit} size="large">
              {t("global.save")}
            </Button>
          </Flex>
        </div>
      </ResponsivePage>
    )
  } else {
    content = (
      <Modal
        width={1000}
        title={isEdit ? t("client.edit") : t("client.add")}
        open={isVisible}
        onCancel={handleCancel}
        className={styles.formModalWrapper}
        footer={
          <Flex gap={10} className={styles.formModalFooter}>
            <Button key="cancel" color="primary" variant="outlined" onClick={handleCancel} size="large">
              {t("global.cancel")}
            </Button>
            <Button key="submit" type="primary" onClick={handleSubmit} size="large">
              {t("global.save")}
            </Button>
          </Flex>
        }
      >
        <CustomForm form={form} layout="vertical" onValuesChange={() => { if (isInitialSetRef.current) setIsDirty(true) }}>
          <ClientForm form={form} />
          {loadingContent}
        </CustomForm>
      </Modal>
    )
  }

  return (
    <>
      {content}

      {/* Discard Confirmation Modal */}
      {isMobile ? (
        <Drawer
          open={showDiscardConfirm}
          placement="bottom"
          height="auto"
          closable={false}
          title={
            <Flex justify="space-between" align="center">
              <div className="flex-1 text-center">{t("Discard Client?")}</div>
              <Button type="text" onClick={handleDiscardCancel} className={`${styles.closeButton} ml-auto p-0`}>
                <CloseOutlined />
              </Button>
            </Flex>
          }
          onClose={handleDiscardCancel}
          footer={
            <Flex vertical gap="small" justify="center">
              <Button className="w-full" key={t("global.no")} color="primary" variant="outlined" onClick={handleDiscardCancel}>
                {t("global.no")}
              </Button>
              <Button className="w-full" key={t("global.yes")} type="primary" danger onClick={handleDiscardConfirm}>
                {t("global.yes")}
              </Button>
            </Flex>
          }
        >
          <div>
            <Flex justify="center" align="center" className={`mb-xl ${styles.warningIcon}`}>
              <img src={warningIcon} alt="warning" width="24" height="24" />
            </Flex>
            <p>{isEdit ? t("global.discard_changes") : t("client.discard_changes")}</p>
          </div>
        </Drawer>
      ) : (
        <Modal
          open={showDiscardConfirm}
          onCancel={handleDiscardCancel}
          className={styles.discardAlertModal}
          closable={false}
          footer={[
            <Button key={t("global.no")} color="primary" variant="outlined" onClick={handleDiscardCancel}>
              {t("global.no")}
            </Button>,
            <Button key={t("global.yes")} type="primary" danger onClick={handleDiscardConfirm}>
              {t("global.yes")}
            </Button>
          ]}
          title={
            <div>
              <Flex className="w-full mb-s" justify="space-between" align="center">
                <Flex justify="center" align="center" className={`w-max ${styles.warningIcon}`}>
                  <img src={warningIcon} alt="warning" width="24" height="24" />
                </Flex>
                <Button type="text" onClick={handleDiscardCancel} className={`${styles.closeButton} p-0`}>
                  <CloseOutlined />
                </Button>
              </Flex>
              {t("client.discard_changes_title")}
            </div>
          }
        >
          <p>{t("global.discard_changes")}</p>
        </Modal>
      )}
    </>
  )
}

export default ClientWindow
